import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { AxiosError } from 'axios';

import { batchResetRejection } from '../../screens/userManagement/request';

interface BatchResetPayload {
  applicantIds: string[];
  reason: string;
}

export const useResetRejectionMutation = (applicantId?: string, handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const mutation = useMutation<APIResponse<any>, AxiosError<ApiErrorResponse>, BatchResetPayload>({
    mutationKey: ['batch-reset-rejection'],
    mutationFn: batchResetRejection,
  });

  const resetRejectionHandler = async (
    values: { applicantId?: string; reason: string },
    resetForm?: () => void,
  ) => {
    const id = values?.applicantId ?? applicantId ?? '';
    const payload: BatchResetPayload = {
      applicantIds: [id],
      reason: values.reason,
    };

    try {
      await mutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: data?.message || 'Rejection reset successfully',
          });

          queryClient.invalidateQueries({ queryKey: ['get-student-users'] });
          queryClient.refetchQueries({ queryKey: ['applicant-study-details', id] });
          queryClient.refetchQueries({ queryKey: ['applicant-personal-info', id] });

          resetForm?.();
          handleClose?.();
        },
      });

      return true;
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description:
          error?.response?.data?.message ||
          error?.response?.data?.title ||
          'Failed to reset rejection',
      });

      return false;
    }
  };

  return {
    resetRejectionHandler,
    isResetRejectionLoading: mutation.isPending,
    isLoading: mutation.isPending,
  };
};
